import { useEffect, useState } from 'react';
import screens from './Screens.module.css';
import styles from './TopPicks.module.css';
import { Button, MatchRow, Spinner } from '../../components/ui';
import { getTopPicks } from '../../api/platform.js';
import { formatMatchTime, useI18n } from '../../i18n/index.jsx';

/**
 * Today's Top Picks — Premium only.
 *
 * The endpoint returns 403 for anyone without Premium, with no partial data, so
 * the gate covers the whole page rather than individual cards. (The earlier
 * "first pick free, rest blurred" treatment isn't possible: the API sends
 * nothing at all to gate against.)
 */
export function TopPicks() {
  const { t, lang } = useI18n();
  const [state, setState] = useState({ status: 'loading' });

  useEffect(() => {
    document.title = `${t('nav_top_picks')} — Don Quack`;
  }, [t]);

  useEffect(() => {
    let cancelled = false;
    getTopPicks()
      .then((res) => {
        if (cancelled) return;
        setState({ status: res.locked ? 'locked' : 'ready', ...res });
      })
      .catch((err) => {
        if (!cancelled) setState({ status: 'error', message: err.message });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section>
      <h2 className={screens.title}>{t('top_picks_title')}</h2>
      <p className={screens.sub}>{t('top_picks_subtitle')}</p>

      {state.status === 'loading' && (
        <div className={screens.loadingBlock}>
          <Spinner />
        </div>
      )}

      {state.status === 'error' && <div className={screens.errorBox}>{state.message}</div>}

      {state.status === 'locked' && (
        <div className={styles.gate}>
          <span className={styles.lock} aria-hidden="true">
            🔒
          </span>
          <h3>{t('top_picks_locked_title')}</h3>
          <p className="muted">{t('top_picks_locked_body')}</p>
          <Button to="/app/pricing">{t('gate_cta')}</Button>
        </div>
      )}

      {state.status === 'ready' &&
        (state.picks.length === 0 ? (
          <div className={screens.empty}>
            <h3>{t('empty_nothing_here')}</h3>
            <p>{t('top_picks_empty')}</p>
            {/* The API filters by the user's own probability threshold and
                league preferences, so an empty list can mean "filtered out"
                rather than "nothing today". */}
            {state.appliedFilters?.total_pool_size > 0 && (
              <p className={styles.filterNote}>
                {state.appliedFilters.after_filters} / {state.appliedFilters.total_pool_size} ·{' '}
                {state.appliedFilters.threshold}%+
              </p>
            )}
          </div>
        ) : (
          <div className={screens.twoCol}>
            {state.picks.map((p) => (
              <MatchRow
                key={p.id}
                league={p.league}
                confidence={p.confidence}
                confidenceLabel={t(`confidence_${p.confidence}_short`)}
                homeTeam={p.home}
                awayTeam={p.away}
                homeProbability={p.homeProbability}
                winner={p.winner}
                predictedWinner={p.predictedWinner}
                kickoff={p.matchStartUtc ? formatMatchTime(p.matchStartUtc, lang) : null}
              />
            ))}
          </div>
        ))}
    </section>
  );
}
